import type { U3202AiModelInput, U3202ActivatedModelRow, U3202DiscoverModelsRequest, U3202OrgProviderConfigFormData } from '../U3202Service';
import { U3202_EMPTY_CAPABILITIES } from './U3202CapabilitiesTypes';

/**
 * Indique si la découverte de modèles est possible (pattern test modèle).
 */
export function u3202CanDiscoverModels(parentData: U3202OrgProviderConfigFormData | null | undefined): boolean {
  if (!parentData) {
    return false;
  }
  const providerCode = parentData.newProviderCode?.trim() || parentData.providerCode?.trim();
  if (!providerCode) {
    return false;
  }
  if (parentData.apiKeyTouched && parentData.apiKey.trim() !== '') {
    return true;
  }
  if (parentData.id || parentData.hasExistingApiKey) {
    return true;
  }
  return parentData.apiKey.trim() !== '';
}

/**
 * Construit le corps de POST /models/discover (clé saisie ou résolue côté backend via providerId).
 */
export function u3202BuildDiscoverModelsRequest(parentData: U3202OrgProviderConfigFormData): U3202DiscoverModelsRequest {
  const request: U3202DiscoverModelsRequest = {
    providerCode: (parentData.newProviderCode?.trim() || parentData.providerCode?.trim() || '').toLowerCase(),
  };
  if (parentData.id) {
    request.providerId = parentData.id;
  }
  if (parentData.apiKeyTouched && parentData.apiKey.trim() !== '') {
    request.apiKey = parentData.apiKey.trim();
  }
  if (parentData.apiUrl.trim() !== '') {
    request.apiUrl = parentData.apiUrl.trim();
  }
  return request;
}

/**
 * Mappe un modèle découvert (API) vers une ligne activée (grille détail).
 */
export function u3202MapDiscoveredModelToActivatedRow(model: U3202AiModelInput): U3202ActivatedModelRow {
  const defaults = model.defaultParameters ?? {};
  return {
    modelId: model.id,
    modelCode: model.code,
    modelName: model.name,
    active: model.active,
    capabilities: model.capabilities ?? U3202_EMPTY_CAPABILITIES,
    catalogActive: model.active,
    modelVersion: 0,
    parameterSchema: model.parameterSchema,
    defaultParameters: defaults,
    parameterValues: { ...defaults },
    initialParameterDefaults: { ...defaults },
    rowState: 'new',
  };
}

/**
 * Fusionne les modèles découverts avec la grille existante (remplacement par code).
 */
export function u3202MergeDiscoveredModels(
  existing: U3202ActivatedModelRow[],
  discovered: U3202AiModelInput[],
): U3202ActivatedModelRow[] {
  const result = [...existing];
  const indexByCode = new Map(result.map((row, index) => [row.modelCode.toLowerCase(), index]));

  for (const model of discovered) {
    const code = model.code.trim().toLowerCase();
    const mapped = u3202MapDiscoveredModelToActivatedRow(model);
    const existingIndex = indexByCode.get(code);
    if (existingIndex !== undefined) {
      const previous = result[existingIndex];
      result[existingIndex] = {
        ...mapped,
        modelId: previous.modelId,
        modelVersion: previous.modelVersion,
        rowState: previous.rowState === 'new' ? 'new' : 'modified',
      };
    } else {
      indexByCode.set(code, result.length);
      result.push(mapped);
    }
  }

  return result;
}
